import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Paginator } from 'primereact/paginator';
import { Calendar } from 'primereact/calendar';
import { ScrollTop } from 'primereact/scrolltop';
import { ProgressBar } from 'primereact/progressbar';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import '../config.js'
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';
        

const ClientLogs = () => {
  // Load the status page script
  useStatusPageScript();
  
  const { type, id, typename } = useParams();
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    log: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS},
    'client.name': { value: null, matchMode: FilterMatchMode.CONTAINS},
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [membership, setMembership] = useState('');
  const [appliedMembership, setAppliedMembership] = useState('');
  const [dataloaded, setDataLoaded] = useState('');
  const [showdebugmessage, setShowDebugMessage] = useState('');
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({
    deniedTitle: 'Client Logs Access Denied',
    deniedMessage: 'You do not have permission to view client logs for this record.',
  });

  useEffect(() => {
    const fetchEvents = async () => {
        getEvents(rows, 0);
    };

    fetchEvents();
  }, [id]);

  const formatQueryDate = (value) => new Date(value).toLocaleString('sv').replace(' ', 'T').split('T')[0];

  const getQueryDateRange = () => {
    const [start, end] = Array.isArray(dateRange) ? dateRange : [];
    const startDate = start || new Date();
    const endDate = end || startDate;

    return {
      initDate: formatQueryDate(startDate),
      endDate: formatQueryDate(endDate),
    };
  };

  const getEvents = async (rows, page, membershipFilter = appliedMembership) => {
    try {
        clearAccessIssue();
        setLoading(true);
        setEvents([]);
        const { initDate, endDate } = getQueryDateRange();
        setShowDebugMessage(`Fetching logs for ${type} with ID ${id} from ${initDate} to ${endDate}`);

        console.log('date range:', initDate, endDate)
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('pageSize', String(rows));
        params.append('initDate', initDate);
        params.append('endDate', endDate);
        if (membershipFilter && membershipFilter.trim()) {
          params.append('membership', membershipFilter.trim());
        }
        const url = global.config.url + `/clientlogs/By${type}/${id}?${params.toString()}`;
        const response = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setEvents(response.data);
          if(response.status === 200){
            if(response.data.length > 0){
              for(let i = 0; i < response.data.length; i++){
                let date = new Date(response.data[i].date + 'Z');
                response.data[i].date = date.toLocaleString("en-GB");
              }
              setDataLoaded('Data loaded correctly');
            }
            else {
              setDataLoaded('No data found');
            }
            setLoading(false);
            const countParams = new URLSearchParams();
            countParams.append('initDate', initDate);
            countParams.append('endDate', endDate);
            if (membershipFilter && membershipFilter.trim()) {
              countParams.append('membership', membershipFilter.trim());
            }
            const urlCount = global.config.url + `/clientlogs/By${type}Count/${id}?${countParams.toString()}`;
            const responseCount = await axios.get(urlCount, {
                headers: {
                Authorization: `Bearer ${token}`,
                },
            });
            setTotalRecords(responseCount.data);
        }
        else {
            setDataLoaded('No information from the server');
            setLoading(false);
            setTotalRecords(0);
            setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching event record:', error);
        setDataLoaded(error.message);
        handleAccessError(error);
          setLoading(false);
          setTotalRecords(0);
          setEvents([]);
      }
    };
  const onPageChange = (event) => {
    setLoading(true);
    setRows(event.rows);
    getEvents(event.rows, event.page, appliedMembership);
    setFirst(event.first);
  };

  const getByDate = () =>{
    const nextMembership = membership.trim();
    setAppliedMembership(nextMembership);
    setLoading(true);
    setFirst(0);
    getEvents(rows, 0, nextMembership)
  }

  const clearFilters = () => {
    setMembership('');
    setAppliedMembership('');
    setDateRange([new Date(), new Date()]);
    setFirst(0);
    setLoading(true);
    getEvents(rows, 0, '');
  };



  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const progressbar = (        
    <div className="card mt-3">
        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>);

  const membershipBodyTemplate = (rowData) => {
    const logText = String(rowData.log || '');
    const query = appliedMembership.trim();
    if (!query) {
      return logText;
    }

    const lowerText = logText.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const start = lowerText.indexOf(lowerQuery);
    if (start < 0) {
      return logText;
    }

    const end = start + query.length;
    return (
      <>
        {logText.slice(0, start)}
        <mark>{logText.slice(start, end)}</mark>
        {logText.slice(end)}
      </>
    );
  };

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  const escapeCsv = (value) => {
    const safeValue = value === null || value === undefined ? '' : value;
    const text = String(safeValue);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const toSafeFilePart = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  };

  const downloadCsv = () => {
    if (!Array.isArray(events) || events.length === 0) {
      return;
    }

    const headers = ['Log', 'Time', 'Client'];
    const rowsCsv = events.map((item) => {
      const client = item && item.client ? item.client : null;
      const row = [
        item && item.log,
        item && item.date,
        client && client.name,
      ];
      return row.map(escapeCsv).join(',');
    });

    const csvContent = [headers.map(escapeCsv).join(','), ...rowsCsv].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const firstItem = events.length > 0 ? events[0] : null;
    const client = firstItem && firstItem.client ? firstItem.client : null;
    const clientName = client && client.name ? client.name : '';
    const contextPart = toSafeFilePart(typename) || toSafeFilePart(clientName) || toSafeFilePart(type) || 'results';

    a.href = url;
    a.download = `client-logs-${contextPart}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className='justify-content-md-center mt-5'>
    <Container fluid="md" >
    <div className="panel">
        <div className="flex align-content-center flex-wrap">
          <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className='flex mb-3'/> 
          <h4 className='flex mt-2 mb-3'>Client logs for '{typename}'</h4> 
          <span className="p-input-icon-left ml-3 mb-3">
              <i className="pi pi-search" />
              <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Filter Search" tooltip="Start typing to filter" 
              tooltipOptions={{position: "bottom"}}/>
          </span>
          <div className="flex align-items-center ml-3 mb-3" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <div className="card flex justify-content-center">
              <Calendar value={dateRange} onChange={(e) => { setDateRange(e.value || [new Date(), new Date()]); setFirst(0); }} selectionMode="range" dateFormat="dd/mm/yy" showIcon  showButtonBar readOnlyInput />
            </div>
            <span className="p-input-icon-left">
              <i className="pi pi-id-card" />
              <InputText value={membership} onChange={(e) => setMembership(e.target.value)} placeholder="Member Number (e.g. BET7153430)" tooltip="Filter by member number" tooltipOptions={{position: "bottom"}}/>
            </span>
            <div className="card flex justify-content-center">
              <Button label="Get" className="p-button-raised" onClick={getByDate} />
            </div>
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button icon="pi pi-times" className="p-button-outlined" onClick={clearFilters} disabled={loading} tooltip="Clear filters" tooltipOptions={{position: "bottom"}} />
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button icon="pi pi-download" className="p-button-outlined" onClick={downloadCsv} disabled={loading || events.length === 0} tooltip="Download current results as CSV" tooltipOptions={{position: "bottom"}} />
          </div>
        </div>

        {appliedMembership.trim() && (
          <div className="ml-3 mb-2">
            <small>Active Member Number filter: {appliedMembership.trim()}</small>
          </div>
        )}

      {/* <Toolbar           
      start={
} 
        end={          
            } /> */}
    </div>
    {loading && progressbar}

    {/* <Row className='justify-content-md-center mt-5'>
      <Col sm={8}>

      </Col>
      <Col sm={4}>

      </Col>
    </Row> */}

    
    {/* <Row className='justify-content-md-center mt-5'>
        <div className="card">
            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
        </div>
    </Row> */}

    <Row className='justify-content-md-center mt-5'>
        <Col>
        {/* <h5 className='text-center'>Debug message: {showdebugmessage}</h5>
        <h5 className='text-center'>{dataloaded}</h5> */}
        <DataTable value={events} selectionMode={'radiobutton'} selection={rowSelectionModel} onSelectionChange={(e) => setRowSelectionModel(e.value.id)} 
        dataKey="id" tableStyle={{ minWidth: '50rem' }} filters={filters} filterDisplay="menu" stripedRows
        globalFilterFields={['log', 'date','client.name']} emptyMessage={loading ? "Loading client logs..." :(appliedMembership.trim() ? "No client logs found for the selected date range and member number." : "No client logs found.")}
        scrollable scrollHeight="60vh">
          <Column field="log" body={membershipBodyTemplate} header="Log" showFilterMenu={false} filterMenuStyle={{ width: '14rem' }} style={{ minWidth: '12rem' }} ></Column>
            <Column field="date" header="Time"></Column>
            <Column field="client.name" header="Client"></Column>
        </DataTable>
        </Col>

    </Row>

    <Row className='justify-content-md-center mt-5'>
        <div className="card">
        <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100, 500]} onPageChange={onPageChange} />
        </div>
    </Row>

    </Container>
    <ScrollTop />
    </Card>
  );
};

export default ClientLogs;
